'use client';

import React, { useRef } from 'react';

import { RoleBasedToggle } from '@ravago/shared/page-data/models/config';
import { ElasticBrand, ElasticCaseStudy, ElasticProduct, OverviewPageItem } from '@ravago/shared/page-data/models/elastic';
import { ProductPortfolioNode } from '@ravago/shared/page-data/models/elements';
import { RendererGlobals } from '@ravago/shared/page-data/models/utils';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';

import { ProductPortfolioPagination } from './product-portfolio-pagination';

import { BrandGrid } from '../overview/brand-grid';
import { CaseStudyGrid } from '../overview/case-study-grid';
import { ProductGrid } from '../overview/product-grid';

interface Props {
   baseRoutes?: {
      brand: string;
      caseStudy: string;
      product: string;
      market: string;
      technology: string;
   };
   relatedDocumentsAnchor?: string;
   node: ProductPortfolioNode;
   globals: RendererGlobals;
   page: number;
   hasPublicLabelAndSigningsToggles: boolean;
   hasSustainabilityLabels: boolean;
   content: Array<OverviewPageItem>;
   onPageChange: (page: number) => void;
   totalPages?: number;
   roleBasedToggles: RoleBasedToggle;
}

export const ProductPortfolioGrid: React.FC<Props> = ({
   baseRoutes,
   content,
   node,
   globals,
   page,
   hasPublicLabelAndSigningsToggles,
   hasSustainabilityLabels,
   onPageChange,
   totalPages = 1,
   roleBasedToggles
}) => {
   const containerRef = useRef<HTMLDivElement>(null);

   const handlePageChange = (page: number) => {
      onPageChange(page);
      if (!containerRef.current) return;

      window.scrollTo({ top: containerRef.current.offsetTop, behavior: 'smooth' });
   };

   return (
      <div ref={containerRef}>
         <Box direction="column">
            {content[0].qualifier === 'product' && (
               <ProductGrid
                  node={node}
                  globals={globals}
                  baseRoutes={baseRoutes}
                  hasPublicLabelAndSigningsPermission={hasPublicLabelAndSigningsToggles}
                  hasSustainabilityLabels={hasSustainabilityLabels}
                  products={content as Array<ElasticProduct>}
                  roleBasedToggles={roleBasedToggles}
               />
            )}
            {content[0].qualifier === 'brand' && (
               <BrandGrid node={node} globals={globals} baseRoute={baseRoutes?.brand} brands={content as Array<ElasticBrand>} />
            )}
            {content[0].qualifier === 'caseStudy' && (
               <CaseStudyGrid node={node} globals={globals} baseRoutes={baseRoutes} caseStudies={content as Array<ElasticCaseStudy>} />
            )}
            <Box justify="end">
               <ProductPortfolioPagination current={page} total={totalPages} onChange={handlePageChange} />
            </Box>
         </Box>
      </div>
   );
};
